import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/api'
import type { CreateVideoTemplateRequest, CustomVideoTemplate, UpdateVideoTemplateRequest } from '@/api/types'
import { useAuth } from './AuthContext'

/**
 * 用户自存的视频模板 —— **唯一的取数入口**。
 *
 * ## 为什么必须是 Context 而不是各页各拉一次
 *
 * 自存模板有两个消费方：视频工作台的模板条（要按档位过滤后渲染，还要参与
 * `activeTemplate()` 的点亮判定）和首页模板中心（要归到「我的模板」那一节、
 * 要能改名删除）。两边各拉一次的话：
 *   · 在模板中心删掉一条，工作台那张卡还在（它还亮着，点下去参数照样铺开）；
 *   · 在工作台刚存一条，回模板中心看不到（下一次路由切换才刷新）。
 * 这不是「多一次请求」的问题，是**同一个东西在两个屏幕上不一样**。
 * 所以清单收在这里，增删改都走这一个地方。
 *
 * ## 失败时退回空清单，而不是报错阻塞
 *
 * 自存模板是**增强**：内置那 8 条不依赖它。接口挂了就让轨道里只有内置模板 ——
 * 工作台照样能用。整页报错反而会把「本来能干活」的场景挡掉。
 * 但 `error` 会往外传，页面可以决定要不要提一句（工作台目前不提，
 * 因为模板条上少几张卡不构成「坏了」）。
 *
 * ## 与账号绑定
 *
 * 模板是按账号收口的（后端每个接口都 `require_user`），所以**换账号必须重拉**。
 * 依赖 `user?.id` 而不是空依赖：不重拉的话，A 退出、B 登录，
 * B 会在工作台看到 A 存的模板 —— 而且点下去就用上了。
 */
type VideoTemplatesContextValue = {
  items: CustomVideoTemplate[]
  /** 第一次拉取是否已结束（成功或失败都算）。**不是**「有没有数据」。 */
  ready: boolean
  error: string | null
  reload: () => Promise<void>
  create: (req: CreateVideoTemplateRequest) => Promise<CustomVideoTemplate>
  update: (id: string, patch: UpdateVideoTemplateRequest) => Promise<CustomVideoTemplate>
  remove: (id: string) => Promise<void>
}

const VideoTemplatesContext = createContext<VideoTemplatesContextValue | null>(null)

export function VideoTemplatesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [items, setItems] = useState<CustomVideoTemplate[]>([])
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inflight = useRef(false)

  const userId = user?.id ?? ''

  const reload = useCallback(async () => {
    if (inflight.current) return
    inflight.current = true
    try {
      const list = await api.listVideoTemplates()
      setItems(list)
      setError(null)
    } catch (e) {
      setItems([])
      setError(e instanceof Error ? e.message : '自存模板拉取失败')
    } finally {
      setReady(true)
      inflight.current = false
    }
  }, [])

  useEffect(() => {
    // 未登录时不请求：后端会 401，而登录页那一屏根本用不到模板。
    // `userId` 变化（换账号 / 退出再登录）会把 ready 复位，避免
    // 上一个人的模板在「正在拉」的窗口里被当成当前账号的。
    if (!userId) {
      setItems([])
      setReady(false)
      return
    }
    setReady(false)
    void reload()
  }, [userId, reload])

  const create = useCallback(async (req: CreateVideoTemplateRequest) => {
    const created = await api.createVideoTemplate(req)
    // 插到最前面：与 `templatesForMode` 的「自存在前」是同一个顺序口径。
    // 不重新拉整份清单 —— 用户的动作刚成功，界面该立刻反映，
    // 而不是再等一个 RTT（中间那段时间卡片是不在的）。
    setItems((l) => [created, ...l])
    return created
  }, [])

  const update = useCallback(async (id: string, patch: UpdateVideoTemplateRequest) => {
    const next = await api.updateVideoTemplate(id, patch)
    setItems((l) => l.map((t) => (t.id === id ? next : t)))
    return next
  }, [])

  const remove = useCallback(async (id: string) => {
    await api.deleteVideoTemplate(id)
    setItems((l) => l.filter((t) => t.id !== id))
  }, [])

  const value = useMemo<VideoTemplatesContextValue>(
    () => ({ items, ready, error, reload, create, update, remove }),
    [items, ready, error, reload, create, update, remove],
  )

  return <VideoTemplatesContext.Provider value={value}>{children}</VideoTemplatesContext.Provider>
}

export function useVideoTemplates() {
  const ctx = useContext(VideoTemplatesContext)
  if (!ctx) throw new Error('useVideoTemplates 必须在 VideoTemplatesProvider 内使用')
  return ctx
}
